import { Alert, App as AntApp, Button, Grid, Space, Table, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { StaffDrawer } from "../components/StaffDrawer.tsx";
import { LinkUserModal } from "../components/LinkUserModal.tsx";
import { AccountCell, ProfileActions, StatusTag } from "../components/ProfileActions.tsx";
import { ProfileListToolbar, type ActiveFilter } from "../components/ProfileListToolbar.tsx";

type StaffMemberDetails = components["schemas"]["StaffMemberDetails"];

export function StaffPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const canManage = hasPermission("people.manage");
  const canLink = canManage && hasPermission("identity.manage");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ActiveFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editing, setEditing] = useState<StaffMemberDetails | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [linking, setLinking] = useState<StaffMemberDetails | null>(null);

  const query = useQuery({
    queryKey: ["staff", { search, filter, page, pageSize }],
    queryFn: async () => {
      const { data, error } = await api.GET("/staff", {
        params: {
          query: { ...(search ? { Search: search } : {}), ...(filter === "all" ? {} : { IsActive: filter === "active" }), Page: page, PageSize: pageSize },
        },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["staff"] });
  const showError = (err: unknown) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" "));

  const toggle = useMutation({
    mutationFn: async (member: StaffMemberDetails) => {
      const path = member.isActive ? "/staff/{id}/deactivate" : "/staff/{id}/reactivate";
      const result = await api.POST(path, { params: { path: { id: member.id } } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await refresh();
      void message.success(t(saved.isActive ? "staff.reactivated" : "staff.deactivated", { name: saved.fullName }));
    },
    onError: showError,
  });

  const unlink = useMutation({
    mutationFn: async (member: StaffMemberDetails) => {
      const result = await api.POST("/staff/{id}/unlink-user", { params: { path: { id: member.id } } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await refresh();
      void message.success(t("person.unlinked", { name: saved.fullName }));
    },
    onError: showError,
  });

  const linkUser = async (userId: string) => {
    if (!linking) return;
    const result = await api.POST("/staff/{id}/link-user", { params: { path: { id: linking.id } }, body: { userId } });
    if (!result.data) throw new ApiError(result.response.status, result.error);
    await refresh();
    void message.success(t("person.linked", { name: result.data.fullName }));
    setLinking(null);
  };

  const columns = [
    {
      title: t("person.columns.name"),
      key: "name",
      render: (_: unknown, member: StaffMemberDetails) => <Typography.Text strong>{member.fullName}</Typography.Text>,
    },
    { title: t("staff.position"), dataIndex: "position", key: "position" },
    { title: t("person.columns.phone"), dataIndex: "phone", key: "phone", responsive: ["md" as const], width: 150 },
    { title: t("person.columns.status"), key: "status", width: 110, render: (_: unknown, member: StaffMemberDetails) => <StatusTag active={member.isActive} /> },
    { title: t("person.columns.account"), key: "account", responsive: ["lg" as const], render: (_: unknown, member: StaffMemberDetails) => <AccountCell user={member.user} /> },
    ...(canManage
      ? [
          {
            title: t("person.columns.actions"),
            key: "actions",
            width: screens.lg ? 360 : 120,
            render: (_: unknown, member: StaffMemberDetails) => (
              <ProfileActions
                row={member}
                deactivateConfirm={t("staff.deactivateConfirm", { name: member.fullName })}
                reactivateConfirm={t("staff.reactivateConfirm", { name: member.fullName })}
                canLink={canLink}
                busy={toggle.isPending && toggle.variables?.id === member.id}
                onEdit={() => {
                  setEditing(member);
                  setDrawerOpen(true);
                }}
                onToggle={() => toggle.mutate(member)}
                onLink={() => setLinking(member)}
                onUnlink={() => unlink.mutate(member)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <ProfileListToolbar
        title={t("staff.title")}
        searchPlaceholder={t("staff.search")}
        filter={filter}
        onFilter={(value) => {
          setFilter(value);
          setPage(1);
        }}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        extra={
          canManage && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              {t("staff.create")}
            </Button>
          )
        }
      />
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<StaffMemberDetails>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("staff.empty") }}
        scroll={{ x: 560 }}
        pagination={{
          current: page,
          pageSize,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: true,
          showTotal: (total) => t("person.total", { count: total }),
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
      />
      <StaffDrawer
        open={drawerOpen}
        member={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={(saved, created) => {
          setDrawerOpen(false);
          void message.success(t(created ? "staff.created" : "staff.saved", { name: saved.fullName }));
        }}
      />
      <LinkUserModal profileName={linking?.fullName ?? null} onClose={() => setLinking(null)} submit={linkUser} />
    </Space>
  );
}
